import useTranslation from 'next-translate/useTranslation';

// Third-party
import clsx from 'clsx';
import { includes } from 'lodash';

// Hooks
import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useToggle from '@/utils/hooks/utils/useToggle';

import type { SocialInboxType } from '@/components/pages/conversations/SocialInbox/types';

// Types/Libs
import pageRights from '@/lib/acl/pageRights';
import { MANAGE_DM_TRACKER } from '@/lib/routes/privateRoutes';
import { INSTAGRAM } from '@/lib/socialProfile';
import {
    DEFAULT_SOCIAL_INBOX_TYPES,
    INSTAGRAM_ONLY_FEATURES,
    SOCIAL_INBOX_MANAGING_OPTIONS
} from '@/components/pages/conversations/SocialInbox/const';

// Components
import Tooltip from '@/components/base/Tooltip';
import { UpsellCrownIcon } from '@/components/composite/Upsell/UpsellCrownIcon/UpsellCrownIcon';
import {
    SocialInboxTitle,
    SocialInboxTypeTitle
} from '@/components/pages/conversations/SocialInbox';
import { DirectMessagesUpsellModal } from '@/components/pages/conversations/SocialInbox/components/DirectMessagesUpsellModal';

interface SocialInboxMenuDesktopProps {
    className?: string;
}

export const SocialInboxMenuDesktop = ({ className }: SocialInboxMenuDesktopProps) => {
    const { t } = useTranslation('conversations');

    const { activeSocialProfile } = useActiveSocialProfile();
    const { currentSocialInbox, navigateTo } = useSocialInboxNavigation();
    const { subscription } = useSubscription();
    const { isOpen: isUpsellModalOpen, toggle: toggleUpsellModal } = useToggle();

    const isInstagram = activeSocialProfile?.social_network_type === INSTAGRAM;
    const planHasAccessToDMs = pageRights[MANAGE_DM_TRACKER]?.plans?.includes(
        subscription?.product_key
    );

    const renderUpsellCrown = (socialInboxType: SocialInboxType) => {
        const isDirectMessages = socialInboxType === 'dm';

        if (isInstagram && isDirectMessages && !planHasAccessToDMs) {
            return <UpsellCrownIcon />;
        }
    };

    const renderMenuItem = (socialInboxType: SocialInboxType) => {
        const isInstagramOnlyFeature = includes(INSTAGRAM_ONLY_FEATURES, socialInboxType);
        const disabled = isInstagramOnlyFeature && !isInstagram;
        const isDirectMessages = socialInboxType === 'dm';

        const handleClick = () => {
            if (isDirectMessages && !planHasAccessToDMs) {
                toggleUpsellModal();
            } else {
                navigateTo(socialInboxType);
            }
        };

        return (
            <Tooltip
                content={t('social_inbox.not_yet_available')}
                interactive={true}
                appendTo={() => document.body}
                placement="right"
                disabled={!disabled}>
                <span className="z-20 flex items-center">
                    <button
                        key={socialInboxType}
                        onClick={handleClick}
                        disabled={disabled}
                        className={clsx(
                            'flex w-full items-center gap-2 space-x-2 overflow-hidden rounded-md p-2',
                            {
                                'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20 dark:text-icoBlue':
                                    currentSocialInbox === socialInboxType,
                                'text-icoGray-500 hover:bg-icoBlue-100 hover:text-icoBlue dark:text-icoDarkMode-wolf dark:hover:bg-icoBlue/20 dark:hover:text-icoBlue':
                                    currentSocialInbox !== socialInboxType,
                                'cursor-not-allowed opacity-50': disabled
                            }
                        )}>
                        <SocialInboxTypeTitle
                            type={socialInboxType}
                            selected={currentSocialInbox === socialInboxType}
                        />

                        {renderUpsellCrown(socialInboxType)}
                    </button>
                </span>
            </Tooltip>
        );
    };

    const renderMenuContainer = (title: string, items: readonly SocialInboxType[]) => {
        return (
            <div className="flex flex-col space-y-2">
                <span className="font-GSemiBold text-xs text-icoBlue dark:text-icoDarkMode-blueStats">
                    {title}
                </span>
                {items.map(renderMenuItem)}
            </div>
        );
    };

    return (
        <div
            className={`flex h-full flex-col space-y-8 bg-white dark:bg-icoDarkMode-deepBlack ${className}`}>
            <SocialInboxTitle />

            {renderMenuContainer(t('social_inbox.inboxes'), DEFAULT_SOCIAL_INBOX_TYPES)}

            {renderMenuContainer(t('social_inbox.managing'), SOCIAL_INBOX_MANAGING_OPTIONS)}

            <DirectMessagesUpsellModal isOpen={isUpsellModalOpen} toggle={toggleUpsellModal} />
        </div>
    );
};
